import React, { useRef, useState } from 'react';
import styles from './entryPhotos.module.css';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import IconClose from '@mui/icons-material/Close';
import IconPhoto from '@mui/icons-material/PhotoCameraOutlined';
import IconGallery from '@mui/icons-material/PhotoLibraryOutlined';
import CustomButton from '../../components/buttons/CustomButton';
import ColorPalette from '../../colorpalette/ColorPalette';
import AttachmentThumb from '../../components/patient-attachments/AttachmentThumb';
import CranialPhotoViewSelect from '../../pages/appointment/components/cranialAsymmetry/photos/CranialPhotoViewSelect';
import { getPhotoView, getViewLabel } from '../../pages/appointment/components/cranialAsymmetry/photos/cranialPhotoViews';
import { prepareUploadItem, releaseUploadItem } from '../PatientAttachmentUploadPopup/uploadAttachments';

const THUMB_SIZE = 84;

/**
 * Área "Fotos desta medição" do popup de medidas: as fotos já gravadas (no modo
 * editar) e as escolhidas agora, cada uma com a vista opcional. O envio é do
 * popup, que só tem o id da medição depois de salvar.
 *
 * @param {Array} photos fotos já gravadas
 * @param {Array} items fotos escolhidas agora (itens de prepareUploadItem)
 * @param {function} onItemsChange (items) => void
 * @param {function} onRemovePhoto (photo) => void, ausente quando não pode excluir
 * @param {boolean} disabled durante o envio
 * @param {number} maxFiles limite por envio
 * @param {function} onError (mensagem) => void
 */
const CranialEntryPhotos = ({ photos, items, onItemsChange, onRemovePhoto, disabled = false, maxFiles = 10, onError }) => {
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const nextId = useRef(1);

    const [preparing, setPreparing] = useState(false);

    const existing = photos || [];
    const chosen = items || [];

    async function filesChosenHandler(event) {
        const files = Array.from(event.target.files || []);
        event.target.value = '';

        if (files.length === 0) return;

        const room = maxFiles - chosen.length;

        if (room <= 0) {
            if (onError) onError(`Até ${maxFiles} fotos por envio.`);
            return;
        }

        const accepted = files.slice(0, room);

        if (accepted.length < files.length && onError) {
            onError(`Até ${maxFiles} fotos por envio.`);
        }

        setPreparing(true);

        try {
            const prepared = [];

            for (const file of accepted) {
                try {
                    prepared.push(await prepareUploadItem(file, nextId.current++));
                } catch (e) {
                    if (onError) onError(`Não foi possível preparar "${file.name}".`);
                }
            }

            onItemsChange(chosen.concat(prepared));
        } finally {
            setPreparing(false);
        }
    }

    function removeItem(id) {
        onItemsChange(chosen.filter((item) => {
            if (item.id === id) releaseUploadItem(item);

            return item.id !== id;
        }));
    }

    function updateItemView(id, view) {
        onItemsChange(chosen.map(item => item.id === id ? { ...item, value: { ...item.value, view: view } } : item));
    }

    return (
        <Box className={styles.photosArea}>
            <Box className={styles.photosHeader}>
                <Typography component="span" className={styles.photosTitle}>
                    Fotos desta medição (opcional)
                </Typography>

                <Typography component="span" className={styles.photosHint}>
                    {`Até ${maxFiles} por envio`}
                </Typography>
            </Box>

            <Box className={styles.photosActions}>
                <CustomButton
                    variant="outlined"
                    style="primary"
                    label="Tirar foto"
                    icon={<IconPhoto />}
                    onClick={() => cameraInputRef.current && cameraInputRef.current.click()}
                    enabled={!disabled}
                />

                <CustomButton
                    variant="outlined"
                    style="primary"
                    label="Escolher fotos"
                    icon={<IconGallery />}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    enabled={!disabled}
                />
            </Box>

            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                style={{ display: 'none' }}
                onChange={filesChosenHandler}
            />

            <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={filesChosenHandler}
            />

            {preparing && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" sx={{ color: ColorPalette.textMedium }}>
                        Preparando fotos…
                    </Typography>
                </Box>
            )}

            {existing.length > 0 && (
                <Box className={styles.photosGrid}>
                    {existing.map((photo) => (
                        <Box key={photo.id} className={styles.photoItem}>
                            <AttachmentThumb
                                attachment={photo}
                                size={THUMB_SIZE}
                                chipLabel={getPhotoView(photo) ? getViewLabel(getPhotoView(photo)) : undefined}
                            />

                            {onRemovePhoto && (
                                <IconButton size="small" onClick={() => onRemovePhoto(photo)} disabled={disabled}>
                                    <IconClose fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                    ))}
                </Box>
            )}

            {chosen.length > 0 && (
                <Box className={styles.photosGrid}>
                    {chosen.map((item) => (
                        <Box key={item.id} className={styles.photoItem}>
                            <div className={styles.photoPreview} style={{ width: THUMB_SIZE, height: THUMB_SIZE }}>
                                {item.previewUrl && <img className={styles.photoPreviewImage} src={item.previewUrl} alt={item.name} />}
                            </div>

                            <CranialPhotoViewSelect
                                value={item.value.view}
                                onChange={(view) => updateItemView(item.id, view)}
                                disabled={disabled}
                                minWidth={140}
                            />

                            <IconButton size="small" onClick={() => removeItem(item.id)} disabled={disabled}>
                                <IconClose fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default CranialEntryPhotos;
